/**
 * Central dispatcher for Paddle checkout events.
 *
 * window.Paddle.Initialize() accepts only ONE eventCallback globally.
 * Multiple components use usePaddleIntegration concurrently, but only
 * one checkout is ever open at a time. This dispatcher holds refs to
 * whichever component called openCheckout() most recently, and routes
 * all Paddle events to that component's handlers.
 */

export interface PaddleCallbackRefs {
  onCheckoutComplete: { current: ((data: any) => void) | undefined };
  onCheckoutError: { current: ((error: any) => void) | undefined };
  onCheckoutPaymentError: { current: ((error: any) => void) | undefined };
  onCheckoutClosed: { current: (() => void) | undefined };
}

let activeRefs: PaddleCallbackRefs | null = null;

/**
 * Register a component's callback refs as the active handler.
 * Called immediately before Paddle.Checkout.open() in openCheckout().
 */
export const setActivePaddleCallbacks = (refs: PaddleCallbackRefs): void => {
  activeRefs = refs;
};

/**
 * Deregister a component's callback refs.
 * Only clears if the provided refs object is the currently active one
 * (identity check prevents a different component from clearing the active handler).
 */
export const clearActivePaddleCallbacks = (refs: PaddleCallbackRefs): void => {
  if (activeRefs === refs) {
    activeRefs = null;
  }
};

/**
 * The single stable function passed to Paddle.Initialize() as eventCallback.
 * Routes events to the currently active component's refs.
 */
export const handlePaddleEvent = (data: any): void => {
  if (!activeRefs) return;

  switch (data?.name) {
    case 'checkout.completed':
      activeRefs.onCheckoutComplete.current?.(data);
      break;
    case 'checkout.error':
      // Let Paddle handle error display in its overlay UI.
      // Do not close checkout or reset state.
      break;
    case 'checkout.payment.error':
      activeRefs.onCheckoutPaymentError.current?.(data);
      break;
    case 'checkout.closed':
      activeRefs.onCheckoutClosed.current?.();
      break;
    default:
      break;
  }
};
